import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { Document, Page } from 'react-pdf';
import { useTranslation } from 'react-i18next';

function getPreviewPageHeight() {
  return Math.max(250, Math.min(720, window.innerHeight - 300));
}

export default function PdfPreviewDialog(props) {
  const {
    open, file, fileName, onClose, onConfirm, confirmLabel, compressionMetadata,
  } = props;
  const { t } = useTranslation();
  const [numPages, setNumPages] = React.useState();
  const [pageNumber, setPageNumber] = React.useState(1);
  const [error, setError] = React.useState(false);
  const [hasRenderedPage, setHasRenderedPage] = React.useState(false);
  const [pageHeight, setPageHeight] = React.useState(getPreviewPageHeight);

  React.useEffect(() => {
    if (open) {
      setNumPages(undefined);
      setPageNumber(1);
      setError(false);
      setHasRenderedPage(false);
    }
  }, [open, file]);

  React.useEffect(() => {
    setError(false);
    setHasRenderedPage(false);
  }, [file, open, pageNumber]);

  React.useEffect(() => {
    const updatePageHeight = () => setPageHeight(getPreviewPageHeight());
    window.addEventListener('resize', updatePageHeight);
    return () => window.removeEventListener('resize', updatePageHeight);
  }, []);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      scroll="paper"
      PaperProps={{
        sx: {
          display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 32px)', maxHeight: 'calc(100dvh - 32px)',
        },
      }}
    >
      <DialogTitle>{t('tank_creation.pdf_preview_title')}</DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flex: '1 1 0',
          flexDirection: 'column',
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'hidden',
          py: 1.5,
        }}
      >
        <Typography sx={{ flexShrink: 0, mb: 1 }}>{fileName}</Typography>
        {compressionMetadata && (
          <Box sx={{ flexShrink: 0, mb: 1 }}>
            <Typography>
              {t('tank_creation.pdf_preview_original_size', {
                originalSize: compressionMetadata.originalSize,
              })}
            </Typography>
            <Typography>
              {t('tank_creation.pdf_preview_final_size', {
                finalSize: compressionMetadata.finalSize,
              })}
            </Typography>
            <Typography>
              {t('tank_creation.pdf_preview_page_count', {
                pageCount: compressionMetadata.pageCount,
              })}
            </Typography>
          </Box>
        )}
        {error && <Alert severity="error">{t('tank_creation.pdf_preview_error')}</Alert>}
        {!error && file && (
          <Box sx={{
            alignItems: 'center', display: 'flex', flex: 1, justifyContent: 'center', minHeight: 0, overflow: 'hidden',
          }}
          >
            <Document
              file={file}
              loading={t('tank_creation.pdf_preview_loading')}
              onLoadError={() => {
                setError(true);
                setHasRenderedPage(false);
              }}
              onLoadSuccess={({ numPages: loadedPageCount }) => setNumPages(loadedPageCount)}
            >
              <Page
                key={pageNumber}
                pageNumber={pageNumber}
                onRenderError={() => {
                  setError(true);
                  setHasRenderedPage(false);
                }}
                onRenderSuccess={() => setHasRenderedPage(true)}
                renderTextLayer={false}
                height={pageHeight}
              />
            </Document>
          </Box>
        )}
        {numPages > 1 && !error && (
          <Box sx={{
            alignItems: 'center', display: 'flex', flexShrink: 0, justifyContent: 'space-between', mt: 1,
          }}
          >
            <Button disabled={pageNumber === 1} onClick={() => setPageNumber((current) => current - 1)}>
              {t('navigation.previous_page')}
            </Button>
            <Typography>{t('tank_creation.pdf_page_number', { pageNumber, numPages })}</Typography>
            <Button disabled={pageNumber === numPages} onClick={() => setPageNumber((current) => current + 1)}>
              {t('navigation.next_page')}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Button onClick={onClose}>{t('close')}</Button>
        {onConfirm && (
          <Button disabled={error || !hasRenderedPage} variant="contained" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
